/**
 * Challenge phase of the step-up authentication modal.
 * 6-digit code entry via Mantine PinInput.
 *
 * Presentational only — the parent modal owns the API calls. Ported from
 * shipstation-lite/src/features/mfa/challenge-phase.tsx. The per-digit inputs,
 * six refs, six state vars and the document-level paste listener from
 * ss-ui-client/src/global/step-up-authentication/phases/challenge.jsx are
 * deliberately deleted rather than translated.
 */

import { useState } from 'react';
import { Anchor, Button, PinInput, Stack, Text } from '@mantine/core';
import { FormattedMessage } from 'react-intl';

import { MfaErrorBanner } from './mfa-error-banner';
import { ModalContent } from '../../components/modal-content';
import { ModalFooter } from '../../components/modal-footer';
import { getChallengeCopy } from '../mfa-factor-messages';
import { getEnrollmentDisplayName, getIsAuthenticator } from '../mfa-helpers';
import { mfaMessages } from '../mfa-messages';

import type { MfaEnrollment } from '../mfa-types';
import type { FormEvent, ReactNode } from 'react';

const MFA_CODE_LENGTH = 6;

export type ChallengePhaseProps = {
  enrollment: MfaEnrollment;
  isSubmitting: boolean;
  errorMessage?: string;
  /** Backend error code, rendered alongside `errorMessage`. */
  errorCode?: string;
  onSubmit: (code: string) => void;
  /** Resend errors are caught and rendered by the parent; this promise must not reject. */
  onResendCode?: () => Promise<void>;
  onChooseDifferentMethod?: () => void;
  hasMultipleEnrollments: boolean;
};

export const ChallengePhase = ({
  enrollment,
  isSubmitting,
  errorMessage,
  errorCode,
  onSubmit,
  onResendCode,
  onChooseDifferentMethod,
  hasMultipleEnrollments,
}: ChallengePhaseProps) => {
  const [code, setCode] = useState('');

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting || code.length !== MFA_CODE_LENGTH) {
      return;
    }
    onSubmit(code);
  };

  const handlePinComplete = (value: string) => {
    setCode(value);
    if (!isSubmitting) {
      onSubmit(value);
    }
  };

  const handleResendCode = () => {
    void onResendCode?.();
  };

  const { titleMessage, subheaderMessage } = getChallengeCopy(enrollment);
  const enrollmentName = getEnrollmentDisplayName(enrollment);
  const shouldShowResendCode = !getIsAuthenticator(enrollment) && onResendCode != null;

  const resendCodeValues = {
    a: (chunks: ReactNode) => (
      <Anchor component='button' disabled={isSubmitting} onClick={handleResendCode} size='xs' type='button'>
        {chunks}
      </Anchor>
    ),
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <ModalContent align='center'>
        <Text fw={600} size='lg'>
          <FormattedMessage {...titleMessage} />
        </Text>

        <Text size='sm' ta='center'>
          <FormattedMessage {...subheaderMessage} />
        </Text>

        {enrollmentName ? (
          <Text c='dimmed' size='sm'>
            {enrollmentName}
          </Text>
        ) : null}

        {errorMessage ? <MfaErrorBanner code={errorCode} message={errorMessage} /> : null}

        <PinInput
          // eslint-disable-next-line jsx-a11y/no-autofocus -- code entry is the sole purpose of this phase; focusing it avoids an extra tab for keyboard and screen-reader users
          autoFocus
          disabled={isSubmitting}
          length={MFA_CODE_LENGTH}
          onChange={setCode}
          onComplete={handlePinComplete}
          oneTimeCode
          size='lg'
          type='number'
          value={code}
        />

        {shouldShowResendCode ? (
          <Stack align='center' gap='xs'>
            <Text c='dimmed' size='xs'>
              <FormattedMessage {...mfaMessages.expiryNotice} />
            </Text>
            <Text size='xs'>
              <FormattedMessage {...mfaMessages.resendCode} values={resendCodeValues} />
            </Text>
          </Stack>
        ) : null}

        {hasMultipleEnrollments && onChooseDifferentMethod ? (
          <Anchor component='button' disabled={isSubmitting} onClick={onChooseDifferentMethod} size='sm' type='button'>
            <FormattedMessage {...mfaMessages.selectDifferentMethodButton} />
          </Anchor>
        ) : null}
      </ModalContent>

      <ModalFooter>
        <Button disabled={isSubmitting || code.length !== MFA_CODE_LENGTH} type='submit'>
          <FormattedMessage {...mfaMessages.submitButton} />
        </Button>
      </ModalFooter>
    </form>
  );
};
